import { z } from 'zod';
import { manifestIdSchema } from './manifest';

/**
 * MOGZO's achievement icons: Lucide icons (lucide.dev, ISC licence) drawn on a colour, so a
 * game needs no art. Names are Lucide's.
 */
export const ACHIEVEMENT_ICONS = [
  'trophy',
  'award',
  'medal',
  'crown',
  'star',
  'sparkles',
  'flame',
  'zap',
  'heart',
  'shield',
  'gem',
  'coins',
  'gift',
  'key',
  'lock',
  'target',
  'flag',
  'rocket',
  'compass',
  'map',
  'mountain',
  'sword',
  'swords',
  'ghost',
  'puzzle',
  'music',
  'clock',
  'timer',
  'sun',
  'moon',
] as const;
export type AchievementIconName = (typeof ACHIEVEMENT_ICONS)[number];

/** The colours a MOGZO icon sits on. */
export const ACHIEVEMENT_COLORS = {
  yellow: '#ffcb2f',
  sky: '#7ad7f0',
  pink: '#f58ab0',
  green: '#9be27a',
  violet: '#c4a7ff',
  orange: '#ff9f5a',
  grey: '#e4e1ee',
  red: '#ff7a7a',
} as const;
export type AchievementColor = keyof typeof ACHIEVEMENT_COLORS;

/** A developer's own icon picture: square, within these sides and size; shown at 256 px. */
export const ICON_RULES = {
  minSide: 128,
  maxSide: 512,
  maxBytes: 256 * 1024,
  side: 256,
  types: ['image/png', 'image/webp', 'image/jpeg'],
} as const;

export const achievementIconSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('mogzo'),
    name: z.enum(ACHIEVEMENT_ICONS),
    color: z.enum(Object.keys(ACHIEVEMENT_COLORS) as [AchievementColor, ...AchievementColor[]]),
  }),
  /** A picture the developer uploaded (an achievement icon upload that's ready). */
  z.object({ kind: z.literal('image'), imageId: z.uuid() }),
  /** No icon: a plain MOGZO badge. */
  z.object({ kind: z.literal('none') }),
]);
export type AchievementIcon = z.infer<typeof achievementIconSchema>;

/**
 * An achievement as the developer makes it in the dashboard (decided Oct 26, 2026). `steps`
 * makes a progress achievement (“tap 100 times”); `hidden` keeps its name and description
 * secret until a player earns it.
 */
export const achievementSettingsSchema = z.object({
  name: z.string().trim().min(1, 'Give the achievement a name').max(60),
  description: z.string().trim().max(200),
  points: z.number().int().min(0).max(100),
  hidden: z.boolean(),
  steps: z.number().int().min(2, 'Steps start at 2').max(1_000_000).nullable(),
  icon: achievementIconSchema,
});
export type AchievementSettings = z.infer<typeof achievementSettingsSchema>;
/** Only the settings to change (the CLI's edit, M18b). */
export const achievementChangesSchema = achievementSettingsSchema.partial();
export const achievementIdSchema = manifestIdSchema;
export const newAchievementSchema = z.intersection(
  z.object({ id: achievementIdSchema }),
  achievementSettingsSchema,
);

/** An achievement as a game sees it, with this player's state. */
export interface AchievementView {
  id: string;
  /** “Hidden achievement” until earned, when hidden. */
  name: string;
  description: string | null;
  points: number;
  hidden: boolean;
  steps: number | null;
  /** A picture to show (PNG/SVG/WebP), or null for a plain badge. */
  icon: string | null;
  progress: number;
  unlocked: boolean;
  unlockedAt: string | null;
}

/** What `unlock` and `progress` answer. */
export interface AchievementResult {
  achievement: AchievementView;
  /** True only the moment it unlocks: show a celebration then, not on repeats. */
  newlyUnlocked: boolean;
}

/** Bridge parameters for the `achievements.*` methods. */
export const achievementParamsSchema = z.object({ id: achievementIdSchema }).strict();
export const achievementProgressParamsSchema = z
  .object({ id: achievementIdSchema, value: z.number().int().min(0).max(1_000_000) })
  .strict();
